import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { tasksAPI } from '../../services/api';
import AdminSidebar from '../../components/AdminSidebar';
import { useToast } from '../../contexts/ToastContext';
import {
  CheckSquare, RefreshCw, Clock, AlertCircle, Check,
  Trash2, Plus, Calendar, ChevronLeft, ChevronRight
} from 'lucide-react';
import '../Admin/AdminDashboard.css';

const PRIORITY_META = {
  high:   { color: '#ef4444', bg: '#ef444420' },
  medium: { color: '#f59e0b', bg: '#f59e0b20' },
  low:    { color: '#6b7280', bg: '#27272a' },
};

const TASK_TYPES = ['follow_up','quote_follow_up','payment_chase','testimonial_request','call','email','meeting','other'];

const AdminTasks = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('today'); // today | upcoming | completed
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ title: '', type: 'follow_up', dueAt: '', priority: 'medium', notes: '', customerEmail: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (tab === 'today') {
        const res = await tasksAPI.getToday();
        setTasks(res.data.tasks || []);
      } else if (tab === 'upcoming') {
        const res = await tasksAPI.getAll({ isCompleted: false, dueAfter: new Date().toISOString(), limit: 50 });
        setTasks(res.data.tasks || []);
      } else {
        const res = await tasksAPI.getAll({ isCompleted: true, limit: 50 });
        setTasks(res.data.tasks || []);
      }
    } catch {
      toast.error('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  }, [tab]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  const completeTask = async (id) => {
    try {
      await tasksAPI.update(id, { isCompleted: true });
      toast.success('Task completed ✓');
      load();
    } catch {
      toast.error('Failed');
    }
  };

  const deleteTask = async (id) => {
    if (!window.confirm('Delete this task?')) return;
    try {
      await tasksAPI.delete(id);
      toast.success('Task deleted');
      load();
    } catch {
      toast.error('Failed to delete');
    }
  };

  const createTask = async () => {
    if (!form.title) return;
    try {
      await tasksAPI.create({ ...form });
      toast.success('Task created');
      setShowNew(false);
      setForm({ title: '', type: 'follow_up', dueAt: '', priority: 'medium', notes: '', customerEmail: '' });
      load();
    } catch {
      toast.error('Failed to create task');
    }
  };

  const isOverdue = (t) => t.dueAt && !t.isCompleted && new Date(t.dueAt) < new Date();

  return (
    <div className="admin-layout">
      <AdminSidebar />
      <main className="admin-content">
        <div className="content-header">
          <div>
            <h1 style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <CheckSquare size={28} color="#f59e0b" /> Tasks
            </h1>
            <p style={{ color: '#a1a1aa' }}>Your follow-ups and reminders</p>
          </div>
          <div className="admin-header-right">
            <button onClick={() => setShowNew(p => !p)} style={{ background: '#f59e0b', border: 'none', borderRadius: 8, padding: '8px 16px', color: '#000', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600 }}>
              <Plus size={15}/> New Task
            </button>
            <button className="btn-secondary" onClick={load}><RefreshCw size={16}/></button>
          </div>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid #27272a', marginBottom: 24 }}>
          {[['today', 'Today & Overdue'], ['upcoming', 'Upcoming'], ['completed', 'Completed']].map(([key, label]) => (
            <button key={key} onClick={() => setTab(key)} style={{
              padding: '10px 20px', background: 'none', border: 'none', cursor: 'pointer',
              color: tab === key ? '#f59e0b' : '#71717a',
              borderBottom: tab === key ? '2px solid #f59e0b' : '2px solid transparent',
              fontSize: 14, fontWeight: tab === key ? 600 : 400,
            }}>{label}</button>
          ))}
        </div>

        {/* New task form */}
        {showNew && (
          <div style={{ background: '#18181b', borderRadius: 12, border: '1px solid #27272a', padding: 20, marginBottom: 20 }}>
            <h3 style={{ margin: '0 0 16px', color: '#fff', fontSize: 16 }}>New Task</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              {[
                { key: 'title', label: 'Title *', type: 'text', full: true },
                { key: 'customerEmail', label: 'Customer Email (optional)', type: 'email' },
                { key: 'type', label: 'Type', type: 'select', options: TASK_TYPES },
                { key: 'dueAt', label: 'Due Date & Time', type: 'datetime-local' },
                { key: 'priority', label: 'Priority', type: 'select', options: ['low','medium','high'] },
                { key: 'notes', label: 'Notes', type: 'text', full: true },
              ].map(f => (
                <label key={f.key} style={{ gridColumn: f.full ? '1 / -1' : undefined, display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13 }}>
                  <span style={{ color: '#71717a' }}>{f.label}</span>
                  {f.type === 'select' ? (
                    <select value={form[f.key]} onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))}
                      style={{ background: '#111', border: '1px solid #27272a', borderRadius: 6, padding: '8px 12px', color: '#fff', fontSize: 13 }}>
                      {f.options.map(o => <option key={o} value={o}>{o.replace(/_/g,' ')}</option>)}
                    </select>
                  ) : (
                    <input type={f.type} value={form[f.key]} onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))}
                      style={{ background: '#111', border: '1px solid #27272a', borderRadius: 6, padding: '8px 12px', color: '#fff', fontSize: 13 }} />
                  )}
                </label>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
              <button onClick={createTask} style={{ background: '#f59e0b', border: 'none', borderRadius: 6, padding: '8px 20px', color: '#000', cursor: 'pointer', fontWeight: 600 }}>Create</button>
              <button onClick={() => setShowNew(false)} style={{ background: '#27272a', border: 'none', borderRadius: 6, padding: '8px 16px', color: '#a1a1aa', cursor: 'pointer' }}>Cancel</button>
            </div>
          </div>
        )}

        {/* Task list */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: 60, color: '#71717a' }}><RefreshCw size={24} style={{ animation: 'spin 1s linear infinite' }} /></div>
        ) : tasks.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 60, color: '#52525b' }}>
            {tab === 'today' ? '🎉 No tasks due today — you\'re clear!' : `No ${tab} tasks`}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {tasks.map(task => {
              const pm = PRIORITY_META[task.priority] || PRIORITY_META.low;
              const overdue = isOverdue(task);
              return (
                <div key={task._id} style={{
                  background: '#18181b',
                  borderRadius: 10,
                  border: `1px solid ${overdue ? '#ef444440' : '#27272a'}`,
                  padding: 16,
                  display: 'flex',
                  gap: 14,
                  alignItems: 'flex-start',
                }}>
                  {/* Complete checkbox */}
                  {!task.isCompleted && (
                    <button
                      onClick={() => completeTask(task._id)}
                      style={{
                        flexShrink: 0, width: 26, height: 26, borderRadius: '50%',
                        border: `2px solid ${overdue ? '#ef4444' : '#27272a'}`,
                        background: 'none', cursor: 'pointer',
                        color: overdue ? '#ef4444' : '#52525b',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        transition: 'all 0.15s',
                        marginTop: 2
                      }}
                      title="Mark complete"
                    >
                      <Check size={13} />
                    </button>
                  )}
                  {task.isCompleted && (
                    <div style={{ flexShrink: 0, width: 26, height: 26, borderRadius: '50%', background: '#10b98120', border: '2px solid #10b981', display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: 2 }}>
                      <Check size={13} color="#10b981" />
                    </div>
                  )}

                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                      <span style={{ fontWeight: 600, color: task.isCompleted ? '#52525b' : '#fff', fontSize: 14, textDecoration: task.isCompleted ? 'line-through' : 'none' }}>
                        {task.title}
                      </span>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        {task.dueAt && (
                          <span style={{ color: overdue ? '#ef4444' : task.isCompleted ? '#52525b' : '#71717a', fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}>
                            <Clock size={11}/>
                            {overdue && !task.isCompleted && '⚠ '}
                            {new Date(task.dueAt).toLocaleString('it-IT', { day:'2-digit', month:'2-digit', hour:'2-digit', minute:'2-digit' })}
                          </span>
                        )}
                        <span style={{ padding: '2px 8px', borderRadius: 8, background: pm.bg, color: pm.color, fontSize: 11, fontWeight: 600 }}>
                          {task.priority}
                        </span>
                      </div>
                    </div>

                    <div style={{ display: 'flex', gap: 12, marginTop: 4, flexWrap: 'wrap' }}>
                      <span style={{ color: '#52525b', fontSize: 12 }}>{task.type?.replace(/_/g,' ')}</span>
                      {task.customerId && (
                        <button
                          onClick={() => navigate(`/admin/customers/${typeof task.customerId === 'object' ? task.customerId._id : task.customerId}`)}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#a78bfa', fontSize: 12, padding: 0 }}
                        >
                          {task.customerId?.name || task.customerId?.email || 'View Customer →'}
                        </button>
                      )}
                    </div>
                    {task.notes && <div style={{ color: '#71717a', fontSize: 12, marginTop: 4 }}>{task.notes}</div>}
                  </div>

                  <button onClick={() => deleteTask(task._id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#3f3f46', padding: 4, flexShrink: 0 }} title="Delete">
                    <Trash2 size={14} />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminTasks;
